import React, { useContext, useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Bell, User, ChevronDown, Settings, LogOut, LogIn } from 'lucide-react';
import { createPageUrl } from '../utils';
import { OrderPageContext } from '@/lib/OrderPageContext';
import { base44 } from '@/api/base44Client';
import { useAuth } from '@/lib/AuthContext';
import { useEventsPage } from '@/lib/useEventsPage';
import { toast } from 'sonner';

export default function DesktopNavbar({ onMenuClick, currentPageName, notificationCount = 0, onNotificationClick, onAccountClick }) {
  const location = useLocation();
  const navigate = useNavigate();
  // Read the signed-in user from AuthContext instead of re-fetching it — the
  // extra base44.auth.me() call logged a 401 for every signed-out visitor.
  const { user, isLoadingAuth } = useAuth();
  const { isPublished: eventsPublished } = useEventsPage();
  const orderPageContext = useContext(OrderPageContext);
  const orderControls = orderPageContext?.orderPageControls;
  const [orderDropdownOpen, setOrderDropdownOpen] = useState(false);
  const [giftCardDropdownOpen, setGiftCardDropdownOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [canAccessAdmin, setCanAccessAdmin] = useState(false);
  const [contactInfo, setContactInfo] = useState(null);
  const dropdownRef = useRef(null);
  const giftCardDropdownRef = useRef(null);

  useEffect(() => {
    loadContactInfo();
  }, []);

  useEffect(() => {
    if (isLoadingAuth) return;

    if (user) {
      setIsAdmin(user.role === 'admin');
      setCanAccessAdmin(false);
      return;
    }

    setIsAdmin(false);
    checkGuestAdmin();
  }, [user, isLoadingAuth]);

  const loadContactInfo = async () => {
    try {
      const data = await base44.entities.ContactInfo.list();
      if (data.length > 0) {
        setContactInfo(data[0]);
      }
    } catch (error) {
      console.error('Error loading contact info:', error);
    }
  };

  // Guest (phone-verified, not signed in) admin access — unchanged behaviour,
  // now only runs when there's genuinely no signed-in user.
  const checkGuestAdmin = async () => {
    const customerData = localStorage.getItem('customer_data');
    if (!customerData) return;
    try {
      const data = JSON.parse(customerData);
      const adminConfig = await base44.entities.AdminConfig.list();
      if (adminConfig.length > 0 && adminConfig[0].admin_phone_number === data.phone_number) {
        setCanAccessAdmin(true);
      }
    } catch {
      setCanAccessAdmin(false);
    }
  };

  const handleLogout = async () => {
    try {
      await base44.auth.logout();
      setIsAdmin(false);
      toast.success('Logged out successfully');
    } catch (error) {
      console.error('Error logging out:', error);
      toast.error('Failed to logout');
    }
  };

  const handleLogin = () => {
    base44.auth.redirectToLogin();
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOrderDropdownOpen(false);
      }
      if (giftCardDropdownRef.current && !giftCardDropdownRef.current.contains(event.target)) {
        setGiftCardDropdownOpen(false);
      }
    };

    if (orderDropdownOpen || giftCardDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [orderDropdownOpen, giftCardDropdownOpen]);

  const navItems = [
  { name: 'Home', path: createPageUrl('Home') },
  { name: 'Allergens', path: createPageUrl('Allergens') },
  { name: 'Book a Table', external: 'https://thebookinhub.peonyhospitalityltd.co.uk/BookingWidget?org=peonygiftskitchen' },
  { name: 'Contact Us', path: createPageUrl('ContactUs') },
  ...(eventsPublished ? [{ name: 'Events', path: createPageUrl('Events') }] : []),
  { name: 'Gift Cards', path: createPageUrl('BuyGiftCard') },
  { name: 'Loyalty', path: createPageUrl('Loyalty') },
  { name: 'Menus', path: createPageUrl('Menus') },
  { name: 'Online Ordering', path: createPageUrl('Order') }];


  const orderingItems = [
  { label: 'Allergens', path: createPageUrl('Allergens') },
  { label: 'Menus', path: createPageUrl('Menus') }];


  const giftCardItems = [
  { label: 'Buy A Gift Card', path: createPageUrl('BuyGiftCard') },
  { label: 'Check A Balance', path: createPageUrl('CheckGiftCardBalance') }];

  const onlineOrderingItems = [
  { label: 'Order', path: createPageUrl('Order') },
  { label: 'Track Order', path: createPageUrl('TrackOrder') }];


  const isActive = (itemName) => {
    const pageName = currentPageName || '';

    // Direct mapping from page names to nav item names
    const pageNameMap = {
      'BookATable': 'Book a Table',
      'ContactUs': 'Contact Us',
      'GiftCards': 'Gift Cards',
      'BuyGiftCard': 'Gift Cards',
      'Home': 'Home',
      'Loyalty': 'Loyalty',
      'Allergens': 'Allergens',
      'Menus': 'Menus',
      'Order': 'Online Ordering'
    };

    const mappedPageName = pageNameMap[pageName] || pageName;
    return mappedPageName === itemName;
  };

  const isOrderingActive = ['Allergens', 'Menus'].includes(currentPageName);
  const isGiftCardActive = ['GiftCards', 'CheckGiftCardBalance'].includes(currentPageName);

  return (
    <>
    <div aria-hidden="true" className="hidden lg:block h-16" />
    <nav className="hidden lg:block bg-white fixed top-0 left-0 right-0 z-50 w-full py-2 border-b border-gray-100">
      <div className="px-4 md:px-6">
        <div className="py-1 flex items-center justify-between gap-10">
          <div className="flex items-center gap-3 md:gap-6">
            {navItems.map((item, index) => {
              // Add social icons after Gift Cards
              const showSocialAfter = item.name === 'Gift Cards' && contactInfo && (contactInfo.facebook || contactInfo.instagram || contactInfo.tiktok);
              if (item.type === 'dropdown') {
                if (item.name === 'Menus & Allergens') {
                  return (
                    <div key="menus-allergens" className="relative group">
                       <button
                        className={`text-sm font-medium transition-colors border-b-2 pb-0 flex items-center gap-2`}
                        style={{
                          color: '#000000',
                          borderBottomColor: isOrderingActive ? '#1A3459' : 'transparent'
                        }}>
                        
                           Menus & Allergens
                         <ChevronDown className="w-4 h-4" />
                       </button>
                       <div className="absolute top-full left-0 pt-1 hidden group-hover:block z-[60]">
                         <div className="bg-white rounded-lg border border-gray-200 shadow-lg min-w-40">
                           {orderingItems.map((orderItem) =>
                          <Link
                            key={orderItem.label}
                            to={orderItem.path}
                            className="block px-4 py-2 text-sm text-black hover:bg-gray-100 first:rounded-t-lg last:rounded-b-lg">
                            
                               {orderItem.label}
                             </Link>
                          )}
                         </div>
                       </div>
                    </div>);

                }

                if (item.name === 'Gift Cards') {
                  return (
                    <div key="giftcards" className="relative group">
                        <button
                        className={`text-sm font-medium transition-colors border-b-2 pb-0 flex items-center gap-2`}
                        style={{
                          color: '#000000',
                          borderBottomColor: isGiftCardActive ? '#1A3459' : 'transparent'
                        }}>

                            Gift Cards
                          <ChevronDown className="w-4 h-4" />
                        </button>
                        <div className="absolute top-full left-0 pt-1 hidden group-hover:block z-[60]">
                          <div className="bg-white rounded-lg border border-gray-200 shadow-lg min-w-48">
                            {giftCardItems.map((giftItem) =>
                          <Link
                            key={giftItem.label}
                            to={giftItem.path}
                            className="block px-4 py-2 text-sm text-black hover:bg-gray-100 first:rounded-t-lg last:rounded-b-lg">

                                {giftItem.label}
                              </Link>
                          )}
                          </div>
                        </div>
                     </div>);

                }

                if (item.name === 'Ordering') {
                  return (
                    <div key="ordering" className="relative group">
                        <button
                        className={`text-sm font-medium transition-colors border-b-2 pb-0 flex items-center gap-2`}
                        style={{
                          color: '#000000',
                          borderBottomColor: isActive(item.name) ? '#1A3459' : 'transparent'
                        }}>

                            Ordering
                          <ChevronDown className="w-4 h-4" />
                        </button>
                        <div className="absolute top-full left-0 pt-1 hidden group-hover:block z-[60]">
                          <div className="bg-white rounded-lg border border-gray-200 shadow-lg min-w-40">
                            {onlineOrderingItems.map((orderItem) =>
                          <Link
                            key={orderItem.label}
                            to={orderItem.path}
                            className="block px-4 py-2 text-sm text-black hover:bg-gray-100 first:rounded-t-lg last:rounded-b-lg">

                                {orderItem.label}
                              </Link>
                          )}
                          </div>
                        </div>
                     </div>);

                }
              }

              const active = isActive(item.name);

              if (item.external) {
                return (
                  <a
                    key={item.name}
                    href={item.external}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm font-medium transition-colors border-b-2 pb-0"
                    style={{ color: '#000000', borderBottomColor: 'transparent' }}>
                    {item.name}
                  </a>);
              }

              return (
                <Link
                  key={item.name}
                  to={item.path}
                  className={`text-sm font-medium transition-colors border-b-2 pb-0`}
                  style={{
                    color: '#000000',
                    borderBottomColor: active ? '#1A3459' : 'transparent'
                  }}>
                  
                  {item.name}
                </Link>);

            })}
          </div>

          <div className="flex items-center gap-2 md:gap-4">
            {isAdmin &&
            <button
              onClick={() => navigate(createPageUrl('AdminSettings'))}
              aria-label="Admin settings"
              className="w-11 h-11 bg-gray-900 rounded-full flex items-center justify-center hover:bg-gray-800 transition-colors">
              
                <Settings aria-hidden="true" className="w-5 h-5 text-white" />
              </button>
            }
            <button
              onClick={() => navigate(createPageUrl('NotificationCenter'))}
              aria-label={notificationCount > 0 ? `Notifications (${notificationCount} unread)` : 'Notifications'}
              className="w-11 h-11 bg-gray-900 rounded-full flex items-center justify-center hover:bg-gray-800 transition-colors relative">
              
              <Bell aria-hidden="true" className="w-5 h-5 text-white" />
              {notificationCount > 0 &&
              <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1.5 bg-red-600 rounded-full text-[10px] font-bold text-white flex items-center justify-center">
                {notificationCount > 99 ? '99+' : notificationCount}
              </span>
              }
            </button>
            <button
              onClick={() => navigate(createPageUrl('Account'))}
              aria-label="Account"
              className="w-11 h-11 bg-gray-900 rounded-full flex items-center justify-center hover:bg-gray-800 transition-colors">
              
              <User aria-hidden="true" className="w-5 h-5 text-white" />
            </button>
          </div>
        </div>
      </div>
    </nav>
    </>);

}